import { containsIgnoreCase } from "../../utils/stringUtils";
import { getPreviousDay } from "../../utils/checkMisthosi";
import { formatYmd, todayYmd } from "../../features/cases/dates";
import { EAUCTION_ACTIONS } from "../../features/cases/listActions";
import AddEauction from "../../components/EauctionAfm/AddEauction";
import { Ellipsis, formatServerDate, parseServerDate } from "../cases/caseColumns";
import UpcomingOverview from "../cases/UpcomingOverview";
import EauctionPanelBody from "./EauctionPanelBody";
import { auctionDatesSummary, auctionsOf, lastPosted, nextAuction } from "./eauctionData";

const text = (key) => ({
  sortValue: (c) => c[key] || "",
  render: (c) => <Ellipsis>{c[key] || ""}</Ellipsis>,
});

const COLUMNS = [
  {
    id: "note",
    flex: true,
    label: "Σημείωση",
    width: 220,
    defaultVisible: true,
    sortValue: (c) => c.note?.trim() || "",
    render: (c) => <Ellipsis>{c.note?.trim() || ""}</Ellipsis>,
  },
  { id: "afm", label: "ΑΦΜ", width: 100, defaultVisible: false, ...text("afm") },
  {
    id: "next_auction",
    label: "Πλειστηριασμός",
    width: 120,
    defaultVisible: true,
    sortValue: (c) => nextAuction(c)?.ymd ?? null,
    render: (c) => {
      const a = nextAuction(c);
      if (!a) return null;
      const upcoming = a.ymd && a.ymd >= todayYmd();
      return (
        <span className={upcoming ? "ds-date-upcoming" : "ds-muted"}>
          {a.ymd ? formatYmd(a.ymd) : a.date}
        </span>
      );
    },
  },
  {
    id: "auctions",
    flex: true,
    label: "Ημ/νίες πλειστηριασμών",
    width: 200,
    defaultVisible: true,
    sortValue: (c) => auctionsOf(c).length || null,
    render: (c) => <Ellipsis>{auctionDatesSummary(c)}</Ellipsis>,
  },
  {
    id: "last_posted",
    label: "Τελ. ανάρτηση",
    width: 110,
    defaultVisible: true,
    sortValue: (c) => lastPosted(c)?.postedYmd ?? null,
    render: (c) => <span>{lastPosted(c)?.posted ?? ""}</span>,
  },
  {
    // Usually empty: auctions are checked in a daily batch (see toolbar).
    id: "last_checked",
    label: "Τελ. έλεγχος",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => parseServerDate(c.last_checked)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.last_checked)}</span>,
  },
  { id: "katastasi", flex: true, label: "Κατάσταση", width: 140, defaultVisible: false, ...text("katastasi") },
  { id: "eidos", flex: true, label: "Είδος", width: 140, defaultVisible: false, ...text("eidos") },
  { id: "topos", flex: true, label: "Τόπος", width: 140, defaultVisible: false, ...text("topos") },
  { id: "timi", label: "Τιμή", width: 110, defaultVisible: false, ...text("timi") },
  {
    id: "created",
    label: "Καταχώρηση",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => parseServerDate(c.created_at)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.created_at)}</span>,
  },
];

const search = (c, q) =>
  containsIgnoreCase(c.note, q) ||
  containsIgnoreCase(String(c.afm ?? ""), q) ||
  containsIgnoreCase(c.unique_id, q) ||
  containsIgnoreCase(auctionDatesSummary(c), q);

const FILTER_KEYS = ["auctions"];

const getFilterDefs = () => [
  {
    key: "auctions",
    label: "Πλειστηριασμοί",
    options: [
      { value: "upcoming", label: "Με επερχόμενο" },
      { value: "yes", label: "Με πλειστηριασμούς" },
      { value: "no", label: "Χωρίς πλειστηριασμούς" },
    ],
  },
];

const applyFilters = (rows, { auctions }) =>
  rows.filter((c) => {
    if (!auctions) return true;
    const list = auctionsOf(c);
    if (auctions === "no") return list.length === 0;
    if (auctions === "yes") return list.length > 0;
    return list.some((a) => a.ymd >= todayYmd());
  });

const label = (c) => c?.note?.trim() || (c?.afm ? `ΑΦΜ ${c.afm}` : "Χωρίς σημείωση");

const getEvents = (c) => {
  // One entry per auction date, however many postings mention it.
  const seen = new Set();
  return auctionsOf(c)
    .filter((a) => a.ymd && !seen.has(a.ymd) && seen.add(a.ymd))
    .map((a) => ({ ymd: a.ymd }));
};

const Overview = ({ rows, onOpen }) => (
  <UpcomingOverview
    rows={rows}
    getEvents={getEvents}
    getLabel={(c) => ({ primary: label(c) })}
    onOpen={onOpen}
    title="Επερχόμενοι πλειστηριασμοί"
    stats={[
      { label: "ΑΦΜ", value: rows.length },
      {
        label: "με επερχόμενο πλειστηριασμό",
        value: rows.filter((c) => auctionsOf(c).some((a) => a.ymd >= todayYmd())).length,
      },
    ]}
  />
);

const AddForm = ({ onClose, onAdded, allCases }) => (
  <AddEauction
    handleClickAddEauctionModal={onClose}
    eauctions={allCases}
    handleAddEauction={() => onAdded(null)}
  />
);

export const EAUCTION_CONFIG = {
  scope: "eauction",
  title: "Eauction",
  noun: { one: "ΑΦΜ", many: "ΑΦΜ", this: "αυτή την εγγραφή" },
  searchPlaceholder: "Αναζήτηση (σημείωση, ΑΦΜ, ημερομηνία)",
  addLabel: "Νέο ΑΦΜ",
  addedText: "Το ΑΦΜ καταχωρήθηκε.",
  emptyText: "Δεν έχετε καταχωρήσει ΑΦΜ προς έλεγχο.",
  columns: COLUMNS,
  columnsById: Object.fromEntries(COLUMNS.map((c) => [c.id, c])),
  getRowLabel: label,
  renderStatus: null,
  search,
  filterKeys: FILTER_KEYS,
  getFilterDefs,
  applyFilters,
  actions: EAUCTION_ACTIONS,
  noteInPanel: true,
  toolbarExtra: () => (
    <span className="ds-count ds-muted">
      Έλεγχος πλειστηριασμών έως <strong>{getPreviousDay()}</strong>
    </span>
  ),
  detail: {
    endpoints: null,
    fullPageHref: null,
    getTitle: label,
    getSubtitle: (c) => (c?.note?.trim() && c?.afm ? `ΑΦΜ ${c.afm}` : ""),
    getRefreshKey: () => "",
    documentTitle: (c) => `${label(c)} | Eauction | MySolon.gr`,
    renderBody: ({ data }) => <EauctionPanelBody data={data} />,
    ariaLabel: "Στοιχεία ΑΦΜ",
  },
  Overview,
  AddForm,
  modalClass: "ds-modal--addcase",
  afterAdd: { refreshDelays: [0] },
};
